// ─── Dashboard feed (analyses.json) ──────────────────────────────────────────

export type Stage3TriggerReason =
  | 'forced'
  | 'alert_yes'
  | 'score_threshold'
  | 'incomplete_pdf_text'
  | 'high_constitutional_relevance'
  | 'mandatory_sensitive_domain'
  | 'excerpt_safety';

export interface FinalDecision {
  stage: string;
  alert: 'yes' | 'no' | string;
  final_risk_level: 'low' | 'medium' | 'high' | string;
  final_score: number;
  summary: string;
  plain_explanation: string;
  reasons: string[];
  constitutional_relevance: 'low' | 'medium' | 'high' | string;
  democratic_impact_areas: string[];
  key_mechanisms: string[];
  abuse_scenarios: Array<string | AbuseScenario>;
  stage3_trigger_reasons?: Stage3TriggerReason[];
  confidence: number;
}

export interface Analysis {
  id: string;
  key: string;
  title: string;
  risk: 'low' | 'medium' | 'high' | string;
  area: string;
  impact: string;
  deadline: string;
  owner: string;
  summary: string;
  detail: string;
  action: string;
  detail_url: string;
  updated_at: string;
  analysis_complete: boolean;
  final_decision?: FinalDecision;
}

export interface FeedMeta {
  scanDate: string;
  sourceName: string;
  feedStatus: string;
  sourceNote?: string;
  sourceScope?: string;
  generatedAt: string;
}

export interface Feed {
  meta: FeedMeta;
  analyses: Analysis[];
}

export type RiskFilter = 'all' | 'low' | 'medium' | 'high' | 'unknown';

// ─── Watchdog published snapshot (watchdog-results.published.json) ────────────

export const WATCHDOG_SCHEMA_VERSION = 'watchdog-results-published-v1' as const;

export type PresentationState =
  | 'completed'
  | 'incomplete'
  | 'no_pdf'
  | 'failed_retryable'
  | 'placeholder'
  | 'error';

export type WatchdogStateFilter = 'all' | 'completed' | 'incomplete' | 'issues';

export interface EvidenceQuote {
  dimension: string;
  quote: string;
  why_it_matters: string;
  truncated?: boolean;
}

export interface FirstPass {
  risk_level: string;
  score: number;
  summary: string;
  plain_explanation: string;
  reasons: string[];
  watch_flags?: string[];
  sensitive_areas?: string[];
  evidence_quotes?: EvidenceQuote[];
  confidence: number;
  placeholder_output?: boolean;
}

export interface SecondPass {
  alert: string;
  final_risk_level: string;
  final_score: number;
  summary: string;
  plain_explanation: string;
  reasons: string[];
  constitutional_relevance: string;
  democratic_impact_areas: string[];
  key_mechanisms: string[];
  abuse_scenarios: Array<string | AbuseScenario>;
  confidence: number;
  placeholder_output?: boolean;
}

export interface ThirdPass {
  final_alert?: string;
  alert: string;
  final_risk_level: string;
  final_score: number;
  summary: string;
  plain_explanation: string;
  reasons: string[];
  constitutional_relevance: string;
  democratic_impact_areas: string[];
  key_mechanisms: string[];
  abuse_scenarios: Array<string | AbuseScenario>;
  confidence: number;
  placeholder_output?: boolean;
}

export interface AbuseScenario {
  scenario_title?: string;
  mechanism?: string;
  trigger_conditions?: string;
  who_could_misuse?: string;
  against_whom?: string;
  realistic_likelihood?: 'low' | 'medium' | 'high' | string;
  why_not_just_theoretical?: string;
  what_would_block_it?: string;
  textual_evidence?: string;
  affected_sections?: string[];
  evidence_type?: string;
}

export interface WatchdogProposal {
  key: string;
  print_number: string;
  title: string;
  detail_url: string;
  queue_status: string;
  processing_status: string;
  processing_stage: string;
  analysis_complete: boolean;
  presentation_state: PresentationState | string;
  stage3_trigger_reasons?: Stage3TriggerReason[];
  updated_at?: string;
  completed_at?: string;
  last_error?: string;
  first_pass?: FirstPass | null;
  second_pass?: SecondPass | null;
  third_pass?: ThirdPass | null;
  final_decision?: FinalDecision | null;
  /** Additive V-20 erratum overlay; never replaces presentation_state/final_decision. */
  correction?: WatchdogCorrection;
}

export interface WatchdogCorrection {
  state: 'degraded' | 'returned_to_curator';
  decision_status: string;
  update_id: number;
  corrected_at?: string | null;
  supersedes_presentation_state?: string;
}

export interface WatchdogPublished {
  schema_version: string;
  generated_at: string;
  source_generated_at: string | null;
  source_runtime_updated_at: string | null;
  published_from: string;
  proposals: WatchdogProposal[];
}
